import {
  useMutation,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "@/core/api";
import type { INote } from "@/customer/customer";
import { customerNotesKey } from "@/customer/query/keys";

export const useGetCustomerNotes = (
  customerId: string,
): UseQueryResult<INote[]> => {
  const { get } = useApi();

  return useQuery<INote[]>({
    queryKey: customerNotesKey(customerId),
    queryFn: async () => {
      const response = await get(`/customers/${customerId}/notes`);
      return response.data;
    },
    enabled: !!customerId,
  });
};

interface AddCustomerNoteParams {
  customerId: string;
  content: string;
}

export const useCreateCustomerNote = () => {
  const { post } = useApi();
  const queryClient = useQueryClient();

  return useMutation<INote, Error, { customerId: string; content: string }>({
    mutationFn: ({ customerId, content }: AddCustomerNoteParams) =>
      post(`/customers/${customerId}/notes`, { note: { content } }).then(
        (response) => response.data as INote,
      ),
    onSuccess: (note, variables) => {
      queryClient.setQueryData<INote[]>(
        customerNotesKey(variables.customerId),
        (oldData = []) => [...oldData, note],
      );
    },
  });
};

interface DeleteCustomerNoteParams {
  customerId: string;
  noteId: string;
}

export const useDeleteCustomerNote = () => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ customerId, noteId }: DeleteCustomerNoteParams) =>
      api.delete(`/customers/${customerId}/notes/${noteId}`),
    onMutate: async ({ customerId, noteId }) => {
      await queryClient.cancelQueries({
        queryKey: customerNotesKey(customerId),
      });
      const previousNotes = queryClient.getQueryData<INote[]>(
        customerNotesKey(customerId),
      );

      queryClient.setQueryData<INote[]>(
        customerNotesKey(customerId),
        (old = []) => old.filter((note) => note.id !== noteId),
      );

      return { previousNotes };
    },
    onError: (_err, variables, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData<INote[]>(
          customerNotesKey(variables.customerId),
          context.previousNotes,
        );
      }
    },
  });
};
